import { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { FolderArchive, Download, Loader2, X, AlertTriangle, RotateCcw } from 'lucide-react';
import { Button } from './ui/button';
import { Progress } from './ui/progress';

const formatSize = (b) => (b ? `${(b / (1024 * 1024)).toFixed(1)} Mo` : '');
const plural = (n, word) => `${n} ${word}${n > 1 ? 's' : ''}`;
const DISMISS_KEY = 'dossiersLotDismissed';
const formatDate = (iso) => (iso ? new Date(iso).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : '');

const JobStatus = ({ job, onDownload, onReset }) => {
  if (job.status === 'en_cours') {
    const pct = job.total ? Math.round((job.done / job.total) * 100) : 0;
    return (
      <div className="flex-1 min-w-[220px]" data-testid="dossiers-lot-progress">
        <div className="flex items-center justify-between text-sm mb-1.5">
          <span className="flex items-center gap-2 font-medium text-[#0F172A]">
            <Loader2 className="h-4 w-4 animate-spin text-[#8DC63F]" />
            <span data-testid="dossiers-lot-count">{job.done} / {plural(job.total, 'dossier')}…</span>
          </span>
          <span className="text-slate-500 truncate ml-2">{job.current}</span>
        </div>
        <Progress value={pct} className="h-2 bg-slate-200 [&>div]:bg-[#8DC63F]" />
      </div>
    );
  }
  if (job.status === 'pret') {
    return (
      <div className="flex flex-wrap items-center gap-3 flex-1">
        <Button onClick={onDownload} className="bg-[#8DC63F] hover:bg-[#7AB62F] text-white" data-testid="dossiers-lot-download-btn">
          <Download className="h-4 w-4 mr-2" /> Télécharger ({plural(job.total, 'dossier')} · {formatSize(job.size)})
        </Button>
        <span className="text-xs text-slate-500" data-testid="dossiers-lot-expiry">Disponible jusqu'au {formatDate(job.expires_at)}</span>
        <button onClick={onReset} className="text-slate-400 hover:text-slate-600" title="Fermer" data-testid="dossiers-lot-close"><X className="h-4 w-4" /></button>
      </div>
    );
  }
  const msg = {
    erreur: `La préparation a échoué${job.error ? ` : ${job.error}` : ''}.`,
    interrompu: 'La préparation a été interrompue (redémarrage du serveur). Relancez-la.',
    expire: "Le fichier n'est plus disponible. Relancez la préparation.",
  }[job.status] || 'Statut inconnu.';
  return (
    <div className="flex items-center gap-3 flex-1 text-sm text-amber-700" data-testid="dossiers-lot-error">
      <AlertTriangle className="h-4 w-4 shrink-0" /> <span>{msg}</span>
      <button onClick={onReset} className="flex items-center gap-1 text-slate-600 hover:text-[#0F172A] underline" data-testid="dossiers-lot-reset"><RotateCcw className="h-3.5 w-3.5" /> OK</button>
    </div>
  );
};

export const SelectCheckbox = ({ checked, onToggle, testId }) => (
  <span
    role="checkbox"
    aria-checked={checked}
    onClick={(e) => { e.preventDefault(); e.stopPropagation(); onToggle(); }}
    className="flex items-center justify-center w-8 h-8 -m-1 rounded-md hover:bg-[#8DC63F]/10 cursor-pointer shrink-0"
    data-testid={testId}
  >
    <input type="checkbox" checked={checked} readOnly tabIndex={-1} className="h-5 w-5 accent-[#8DC63F] pointer-events-none" />
  </span>
);

export const DossiersLotBar = ({ source, selectedIds, onClear, extraActions = null }) => {
  const [job, setJob] = useState(null);
  const [starting, setStarting] = useState(false);
  const timer = useRef(null);

  const stopPolling = () => { if (timer.current) clearInterval(timer.current); timer.current = null; };

  const poll = useCallback((jobId) => {
    stopPolling();
    timer.current = setInterval(async () => {
      try {
        const { data } = await axios.get(`/dossiers-lot/${jobId}`);
        setJob(data);
        if (data.status !== 'en_cours') {
          stopPolling();
          if (data.status === 'pret') toast.success('Les dossiers sont prêts à être téléchargés');
        }
      } catch (e) {
        stopPolling();
      }
    }, 2000);
  }, []);

  useEffect(() => {
    axios.get('/dossiers-lot/recent', { params: { source } }).then(({ data }) => {
      if (data && ['en_cours', 'pret'].includes(data.status) && localStorage.getItem(DISMISS_KEY) !== data.job_id) {
        setJob(data);
        if (data.status === 'en_cours') poll(data.job_id);
      }
    }).catch(() => {});
    return stopPolling;
  }, [source, poll]);

  const start = async () => {
    setStarting(true);
    try {
      const { data } = await axios.post('/dossiers-lot', { user_ids: selectedIds, source });
      setJob(data);
      onClear();
      poll(data.job_id);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Impossible de lancer la préparation');
    } finally {
      setStarting(false);
    }
  };

  const download = () => {
    const link = document.createElement('a');
    link.href = `${axios.defaults.baseURL || ''}/dossiers-lot/${job.job_id}/download?token=${job.download_token}`;
    link.setAttribute('download', job.filename || 'Dossiers.zip');
    document.body.appendChild(link);
    link.click();
    link.remove();
  };

  const busy = job?.status === 'en_cours';
  if (!job && selectedIds.length === 0) return null;

  return (
    <div className="sticky top-2 z-20 flex flex-wrap items-center gap-3 bg-white/95 backdrop-blur-md border border-[#8DC63F]/40 shadow-lg rounded-xl px-4 py-3" data-testid="dossiers-lot-bar">
      {selectedIds.length > 0 && !busy && (
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm font-medium text-[#0F172A]" data-testid="dossiers-lot-selected-count">{selectedIds.length} sélectionné(s)</span>
          <Button onClick={start} disabled={starting} className="bg-[#0F172A] hover:bg-[#1E293B] text-white" size="sm" data-testid="dossiers-lot-start-btn">
            {starting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <FolderArchive className="h-4 w-4 mr-2" />}
            Télécharger les dossiers complets
          </Button>
          {extraActions}
          <Button onClick={onClear} variant="ghost" size="sm" className="text-slate-500" data-testid="dossiers-lot-clear-btn">Tout décocher</Button>
        </div>
      )}
      {job && <JobStatus job={job} onDownload={download} onReset={() => { localStorage.setItem(DISMISS_KEY, job.job_id); setJob(null); }} />}
    </div>
  );
};
